import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Drawer, 
  Table, 
  Input, 
  Select, 
  Button, 
  Tag, 
  Space, 
  Modal, 
  Form, 
  Checkbox,
  message,
  Card,
  Skeleton,
  notification,
  Dropdown
} from 'antd';
import { SearchOutlined, EditOutlined, CheckOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { membersAPI, accountsAPI } from '../services/api';
import Loader from '../components/Loader';
import { useDownloadProgress } from '../contexts/DownloadProgressContext';
import './Members.css';

const { Search } = Input;
const { Option } = Select;

// Custom hook for debounced value
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

interface Member {
  phoneNumber: string;
  name: string;
  id: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  groups: Array<{
    groupId: string;
    groupName: string;
    accountId: string;
    accountName: string;
  }>;
}

interface GroupDetail {
  groupId: string;
  groupName: string;
  accountId: string;
  accountName: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

interface Account {
  _id: string;
  phoneNumber: string;
  accountName: string;
  status: string;
  qrCode?: string | null;
  lastSync?: string | null;
  groupsCount?: number;
  membersCount?: number;
}

const Members: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms debounce
  const [selectedAccount, setSelectedAccount] = useState<string>('all');
  const [minGroups, setMinGroups] = useState<number | undefined>(undefined);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  
  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [memberGroups, setMemberGroups] = useState<GroupDetail[]>([]);
  
  // Drawer filters
  const [drawerSearchTerm, setDrawerSearchTerm] = useState('');
  const [drawerAccountFilter, setDrawerAccountFilter] = useState<string>('all');
  const [drawerRoleFilter, setDrawerRoleFilter] = useState<string>('all');
  
  // Edit modal state
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [form] = Form.useForm();
  
  // CSV download state - use global context
  const {
    setDownloadProgress,
    isDownloading: isDownloadingCSV,
    setIsDownloading: setIsDownloadingCSV,
    downloadInProgressRef,
  } = useDownloadProgress();

  // Memoized query keys - prevents unnecessary refetches when values haven't changed
  const membersQueryKey = useMemo(
    () => ['members', 'active', currentPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups],
    [currentPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups]
  );
  const archivedMembersQueryKey = useMemo(
    () => ['members', 'archived', currentPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups],
    [currentPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups]
  );
  const accountsQueryKey = useMemo(() => ['accounts'], []);
  
  // Memoized query params to prevent unnecessary API calls
  const activeMembersParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      limit: pageSize,
    };
    
    if (debouncedSearchTerm) {
      params.search = debouncedSearchTerm;
    }
    
    if (selectedAccount !== 'all') {
      params.accountId = selectedAccount;
    }
    
    if (minGroups) {
      params.minGroups = minGroups;
    }
    
    return params;
  }, [currentPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups]);
  
  const archivedMembersParams = useMemo(() => {
    // Same params structure
    return activeMembersParams;
  }, [activeMembersParams]);
  
  // Memoized prefetch function
  const prefetchNextPage = useCallback(() => {
    const nextPage = currentPage + 1;
    const nextPageParams = { ...activeMembersParams, page: nextPage };
    const queryKey = activeTab === 'active' 
      ? ['members', 'active', nextPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups]
      : ['members', 'archived', nextPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups];
    
    queryClient.prefetchQuery({
      queryKey,
      queryFn: async () => {
        const response = activeTab === 'active' 
          ? await membersAPI.getAll(nextPageParams)
          : await membersAPI.getArchived(nextPageParams);
        return response.data;
      },
      staleTime: 2 * 60 * 1000,
    });
  }, [currentPage, pageSize, debouncedSearchTerm, selectedAccount, minGroups, activeTab, queryClient, activeMembersParams]);

  // Fetch accounts - memoized to prevent unnecessary refetches
  const { data: accountsData } = useQuery({
    queryKey: accountsQueryKey,
    queryFn: async () => {
      const response = await accountsAPI.getAll();
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
  });
  
  // Memoized accounts array
  const accounts = useMemo(() => accountsData || [], [accountsData]);

  // Memoized query function for active members
  const fetchActiveMembers = useCallback(async () => {
    const response = await membersAPI.getAll(activeMembersParams);
    return response.data;
  }, [activeMembersParams]);

  // Fetch active members
  const { 
    data: membersData, 
    isLoading: loading, 
    isFetching: isFetchingActive,
    error: membersError 
  } = useQuery({
    queryKey: membersQueryKey,
    queryFn: fetchActiveMembers,
    enabled: activeTab === 'active',
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData, // Prevent flickering when paginating
    refetchOnWindowFocus: false,
  });

  // Memoized query function for archived members
  const fetchArchivedMembers = useCallback(async () => {
    const response = await membersAPI.getArchived(archivedMembersParams);
    return response.data;
  }, [archivedMembersParams]);

  // Fetch archived members
  const { 
    data: archivedMembersData, 
    isLoading: archivedLoading, 
    isFetching: isFetchingArchived,
    error: archivedError 
  } = useQuery({
    queryKey: archivedMembersQueryKey,
    queryFn: fetchArchivedMembers,
    enabled: activeTab === 'archived',
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData, // Prevent flickering when paginating
    refetchOnWindowFocus: false,
  });
  
  // Prefetch next page when data is loaded
  useEffect(() => {
    const currentData = activeTab === 'active' ? membersData : archivedMembersData;
    const total = (currentData as any)?.pagination?.total || 0;
    const totalPages = Math.ceil(total / pageSize);
    
    // Prefetch next page if not on last page
    if (currentPage < totalPages && currentData) {
      prefetchNextPage();
    }
  }, [membersData, archivedMembersData, currentPage, pageSize, activeTab, prefetchNextPage]);

  // Ensure progress indicator persists during pagination changes
  useEffect(() => {
    // Progress state is managed independently, so it should persist
    // This effect ensures the progress card remains visible even when other state changes
  }, [currentPage, pageSize, activeTab]);

  // Memoized computed values
  const members = useMemo(() => (membersData as any)?.members || [], [membersData]);
  const total = useMemo(() => (membersData as any)?.pagination?.total || 0, [membersData]);
  const archivedMembers = useMemo(() => (archivedMembersData as any)?.members || [], [archivedMembersData]);
  const archivedTotal = useMemo(() => (archivedMembersData as any)?.pagination?.total || 0, [archivedMembersData]);

  // Deduplicate members by phoneNumber or id (same person can appear in multiple groups)
  const uniqueMembers = useMemo(() => {
    if (!members.length) return [];
    const seen = new Set<string>();
    return members.filter((m: any) => {
      const key = m.phoneNumber || m.id || '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [members]);
  const uniqueArchivedMembers = useMemo(() => {
    if (!archivedMembers.length) return [];
    const seen = new Set<string>();
    return archivedMembers.filter((m: any) => {
      const key = m.phoneNumber || m.id || '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [archivedMembers]);
  const isLoading = useMemo(() => activeTab === 'active' ? loading : archivedLoading, [activeTab, loading, archivedLoading]);
  const isFetching = useMemo(() => activeTab === 'active' ? isFetchingActive : isFetchingArchived, [activeTab, isFetchingActive, isFetchingArchived]);
  const error = useMemo(() => 
    activeTab === 'active' 
      ? (membersError as any)?.response?.data?.error || (membersError ? 'Failed to fetch members' : null)
      : (archivedError as any)?.response?.data?.error || (archivedError ? 'Failed to fetch archived members' : null),
    [activeTab, membersError, archivedError]
  );

  // Fetch member details query
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);
  
  const { data: memberDetailsData } = useQuery({
    queryKey: ['member', selectedPhoneNumber],
    queryFn: async () => {
      if (!selectedPhoneNumber) return null;
      const encodedPhoneNumber = encodeURIComponent(selectedPhoneNumber);
      const response = await membersAPI.getByPhoneNumber(encodedPhoneNumber);
      return response.data;
    },
    enabled: !!selectedPhoneNumber && drawerVisible,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Memoized drawer handlers
  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setSelectedMember(null);
    setSelectedPhoneNumber(null);
    setMemberGroups([]);
    setDrawerSearchTerm('');
    setDrawerAccountFilter('all');
    setDrawerRoleFilter('all');
  }, []);

  const handleDrawerSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setDrawerSearchTerm(e.target.value);
  }, []);

  // Memoized handleViewGroups to prevent unnecessary re-renders
  const handleViewGroups = useCallback((member: Member) => {
    setSelectedMember(member);
    setSelectedPhoneNumber(member.phoneNumber);
    setDrawerVisible(true);
    
    // Use groups from table data if available, otherwise will be fetched via query
    if (member.groups && member.groups.length > 0) {
      // Map groups to include isAdmin and isSuperAdmin from member if not present
      const groupsWithRoles: GroupDetail[] = member.groups.map(group => ({
        ...group,
        isAdmin: (group as any).isAdmin ?? member.isAdmin,
        isSuperAdmin: (group as any).isSuperAdmin ?? member.isSuperAdmin,
      }));
      setMemberGroups(groupsWithRoles);
    } else {
      setMemberGroups([]); // Clear previous groups while loading
    }
    
    // Reset drawer filters
    setDrawerSearchTerm('');
    setDrawerAccountFilter('all');
    setDrawerRoleFilter('all');
  }, []);

  // Update memberGroups when detailed data is fetched (if table data didn't have groups)
  React.useEffect(() => {
    if (memberDetailsData?.groups && memberDetailsData.groups.length > 0) {
      setMemberGroups(memberDetailsData.groups);
    }
  }, [memberDetailsData]);

  // Filter groups in drawer
  const filteredDrawerGroups = useMemo(() => {
    let filtered = [...memberGroups];

    // Apply search filter
    if (drawerSearchTerm) {
      const searchLower = drawerSearchTerm.toLowerCase();
      filtered = filtered.filter(
        (group) =>
          group.groupName.toLowerCase().includes(searchLower) ||
          (group.accountName || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply account filter
    if (drawerAccountFilter !== 'all') {
      filtered = filtered.filter(
        (group) => group.accountId === drawerAccountFilter
      );
    }

    // Apply role filter
    if (drawerRoleFilter !== 'all') {
      if (drawerRoleFilter === 'superAdmin') {
        filtered = filtered.filter((group) => group.isSuperAdmin);
      } else if (drawerRoleFilter === 'admin') {
        filtered = filtered.filter((group) => group.isAdmin && !group.isSuperAdmin);
      } else if (drawerRoleFilter === 'member') {
        filtered = filtered.filter((group) => !group.isAdmin && !group.isSuperAdmin);
      }
    }

    return filtered;
  }, [memberGroups, drawerSearchTerm, drawerAccountFilter, drawerRoleFilter]);

  // Memoized drawer account options
  const drawerAccountOptions = useMemo(() => 
    Array.from(new Set(memberGroups.map(g => g.accountId)))
      .filter((id: string) => id)
      .map((accountId: string) => {
        const account = accounts.find((a: Account) => a._id === accountId);
        const group = memberGroups.find(g => g.accountId === accountId);
        return {
          key: accountId,
          value: accountId,
          label: account?.accountName || group?.accountName || 'Unknown'
        };
      }),
    [memberGroups, accounts]
  );

  // Memoized drawer table columns
  const drawerTableColumns = useMemo(() => [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: 'Group Name',
      dataIndex: 'groupName',
      key: 'groupName',
    },
    {
      title: 'Account',
      dataIndex: 'accountName',
      key: 'accountName',
    },
    {
      title: 'Role',
      key: 'role',
      width: 120,
      render: (record: GroupDetail) => (
        <Space>
          {record.isSuperAdmin && <Tag color="red">Super Admin</Tag>}
          {record.isAdmin && !record.isSuperAdmin && <Tag color="blue">Admin</Tag>}
          {!record.isAdmin && !record.isSuperAdmin && <Tag>Member</Tag>}
        </Space>
      ),
    },
  ], []);

  const handleEdit = useCallback((member: Member) => {
    setEditingMember(member);
    form.setFieldsValue({
      name: member.name,
      isAdmin: member.isAdmin,
      isSuperAdmin: member.isSuperAdmin,
    });
    setEditModalVisible(true);
  }, [form]);

  // Update member mutation
  const updateMemberMutation = useMutation({
    mutationFn: async ({ phoneNumber, data }: { phoneNumber: string; data: { name: string; isAdmin?: boolean; isSuperAdmin?: boolean } }) => {
      const encodedPhoneNumber = encodeURIComponent(phoneNumber);
      return await membersAPI.update(encodedPhoneNumber, data);
    },
    onSuccess: () => {
      message.success('Member updated successfully');
      setEditModalVisible(false);
      setEditingMember(null);
      form.resetFields();
      
      // Invalidate and refetch queries - more targeted invalidation
      queryClient.invalidateQueries({ queryKey: ['members'] });
      if (selectedPhoneNumber) {
        queryClient.invalidateQueries({ queryKey: ['member', selectedPhoneNumber] });
      }
      // Also invalidate current page query
      queryClient.invalidateQueries({ 
        queryKey: activeTab === 'active' ? membersQueryKey : archivedMembersQueryKey 
      });
    },
    onError: (err: any) => {
      message.error(err.response?.data?.error || 'Failed to update member');
    },
  });

  // Memoized handleUpdateMember
  const handleUpdateMember = useCallback(async () => {
    try {
      const values = await form.validateFields();
      
      if (!editingMember) return;
      
      updateMemberMutation.mutate({
        phoneNumber: editingMember.phoneNumber,
        data: {
          name: values.name,
          isAdmin: values.isAdmin,
          isSuperAdmin: values.isSuperAdmin,
        }
      });
    } catch (err) {
      // Form validation error
    }
  }, [form, editingMember, updateMemberMutation]);

  // Memoized modal close handler
  const handleModalClose = useCallback(() => {
    setEditModalVisible(false);
    setEditingMember(null);
    form.resetFields();
  }, [form]);

  // Memoized tab change handlers
  const handleActiveTabClick = useCallback(() => {
    setActiveTab('active');
    setCurrentPage(1);
  }, []);

  const handleArchivedTabClick = useCallback(() => {
    setActiveTab('archived');
    setCurrentPage(1);
  }, []);

  // Memoized refresh handler
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: activeTab === 'active' ? membersQueryKey : archivedMembersQueryKey 
    });
  }, [queryClient, activeTab, membersQueryKey, archivedMembersQueryKey]);

  // Memoized filter handlers to prevent unnecessary re-renders
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleSearchEnter = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setSearchTerm(target.value);
    setCurrentPage(1);
  }, []);

  const handleAccountChange = useCallback((value: string) => {
    setSelectedAccount(value);
    setCurrentPage(1);
  }, []);

  const handleMinGroupsChange = useCallback((value: string | number) => {
    setMinGroups(value === 'all' ? undefined : Number(value));
    setCurrentPage(1);
  }, []);

  const handlePageSizeChange = useCallback((value: number) => {
    setPageSize(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Use server export CSV endpoint for full data (no pagination limits)
  const downloadAllData = useCallback(async (includeGroups = true) => {
    if (isDownloadingCSV) return; // Prevent multiple simultaneous downloads

    try {
      setIsDownloadingCSV(true);
      downloadInProgressRef.current = true;
      setDownloadProgress({
        currentPage: 0,
        totalPages: 0,
        membersCount: 0,
        stage: 'streaming'
      });

      const params: { search?: string; accountId?: string; minGroups?: number; type: 'active' | 'archived'; includeGroups?: boolean } = {
        type: activeTab,
        includeGroups
      };
      if (debouncedSearchTerm) params.search = debouncedSearchTerm;
      if (selectedAccount !== 'all') params.accountId = selectedAccount;
      if (minGroups) params.minGroups = minGroups;

      const response = await membersAPI.exportCSV(params, {
        onDownloadProgress: (e) => {
          setDownloadProgress({
            currentPage: 0,
            totalPages: 0,
            membersCount: 0,
            stage: 'streaming',
            bytesLoaded: e.loaded
          });
        }
      });

      if (!downloadInProgressRef.current) {
        return;
      }

      const blob = response.data as Blob;
      if (!blob || blob.size === 0) {
        setDownloadProgress(null);
        downloadInProgressRef.current = false;
        notification.warning({
          message: 'No Members Found',
          description: 'No members found to export with the current filters.',
          placement: 'topRight',
          duration: 4,
        });
        return;
      }

      // Check for error response in blob (e.g. server returned JSON error)
      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const text = await blob.text();
        const errData = JSON.parse(text);
        throw new Error(errData.error || 'Export failed');
      }

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      const suffix = includeGroups ? 'full' : 'quick';
      link.setAttribute('download', `members_${activeTab}_${suffix}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show 100% complete in loader before closing
      setDownloadProgress({
        currentPage: 0,
        totalPages: 0,
        membersCount: 0,
        stage: 'generating',
      });
      downloadInProgressRef.current = false;
      setTimeout(() => {
        setDownloadProgress(null);
        notification.success({
          message: 'Download Complete',
          description: 'Successfully exported full members data to CSV (no limit).',
          placement: 'topRight',
          duration: 5,
        });
      }, 700);
    } catch (err: any) {
      setDownloadProgress(null);
      downloadInProgressRef.current = false;
      const message = err.response?.data instanceof Blob
        ? 'Export failed. Please try again.'
        : (err.response?.data?.error || err.message || 'Failed to download data. Please try again.');
      notification.error({
        message: 'Download Failed',
        description: message,
        placement: 'topRight',
        duration: 5,
      });
    } finally {
      setIsDownloadingCSV(false);
    }
  }, [activeTab, isDownloadingCSV, debouncedSearchTerm, selectedAccount, minGroups, setDownloadProgress, downloadInProgressRef, setIsDownloadingCSV]);

  // Use handleViewGroups directly since it's already memoized
  const handleViewGroupsCallback = handleViewGroups;

  const columns = useMemo(() => [
    {
      title: '#',
      key: 'index',
      width: 60,
      render: (_: any, __: any, index: number) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Member, b: Member) => a.name.localeCompare(b.name),
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      sorter: (a: Member, b: Member) => a.phoneNumber.localeCompare(b.phoneNumber),
    },
    {
      title: 'Groups Count',
      key: 'groupsCount',
      width: 120,
      render: (record: Member) => (
        <Button
          type="link"
          onClick={() => handleViewGroupsCallback(record)}
          className="groups-count-btn"
        >
          {record.groups.length} groups
        </Button>
      ),
      sorter: (a: Member, b: Member) => a.groups.length - b.groups.length,
    },
    {
      title: 'Status',
      key: 'status',
      width: 150,
      render: (record: Member) => (
        <Space>
          {record.isSuperAdmin && <Tag color="red">Super Admin</Tag>}
          {record.isAdmin && !record.isSuperAdmin && <Tag color="blue">Admin</Tag>}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (record: Member) => (
        <Button
          type="primary"
          icon={<EditOutlined />}
          size="small"
          onClick={() => handleEdit(record)}
        >
          Edit
        </Button>
      ),
    },
  ], [currentPage, pageSize, handleViewGroupsCallback, handleEdit]);

  return (
    <div className="members-page">
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>Members</h1>
          <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
            Total: {activeTab === 'active' ? total.toLocaleString() : archivedTotal.toLocaleString()} {activeTab === 'active' ? 'active' : 'archived'} members
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Button 
            type={activeTab === 'active' ? 'primary' : 'default'}
            onClick={handleActiveTabClick}
          >
            Active Members
          </Button>
          <Button 
            type={activeTab === 'archived' ? 'primary' : 'default'}
            onClick={handleArchivedTabClick}
          >
            Archived Members
          </Button>
          <Button 
            onClick={handleRefresh}
            loading={isFetching}
          >
            Refresh
          </Button>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'full',
                  label: 'Full export (with group names)',
                  onClick: () => downloadAllData(true),
                },
                {
                  key: 'quick',
                  label: 'Quick export (no group names, faster)',
                  onClick: () => downloadAllData(false),
                },
              ],
            }}
            disabled={isDownloadingCSV || isFetching}
          >
            <Button 
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloadingCSV}
              title={isFetching ? 'Please wait while data is refreshing...' : undefined}
            >
              {isDownloadingCSV ? 'Downloading...' : 'Download'} <DownOutlined />
            </Button>
          </Dropdown>
          <Button
            icon={<DownloadOutlined />}
            onClick={() => downloadAllData(false)}
            loading={isDownloadingCSV}
            disabled={isDownloadingCSV || isFetching}
            title="Fast export without group names column"
          >
            Quick export
          </Button>
        </div>
      </div>

      <Card className="filters-card">
        <div className="filters-section">
          <div className="filter-group">
            <label>Search:</label>
            <Search
              placeholder="Search by name or phone number"
              allowClear
              value={searchTerm}
              onChange={handleSearchChange}
              onPressEnter={handleSearchEnter}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
            />
          </div>

          <div className="filter-group">
            <label>Account:</label>
            <Select
              value={selectedAccount}
              onChange={handleAccountChange}
              style={{ width: 200 }}
            >
              <Option value="all">All Accounts</Option>
              {accounts.map((account: Account) => (
                <Option key={account._id} value={account._id}>
                  {account.accountName} {account.status && `(${account.status})`}
                </Option>
              ))}
            </Select>
          </div>

          <div className="filter-group">
            <label>Min Groups:</label>
            <Select
              value={minGroups || 'all'}
              onChange={handleMinGroupsChange}
              style={{ width: 150 }}
            >
              <Option value="all">All</Option>
              <Option value={1}>1+ Groups</Option>
              <Option value={2}>2+ Groups</Option>
              <Option value={3}>3+ Groups</Option>
              <Option value={5}>5+ Groups</Option>
              <Option value={10}>10+ Groups</Option>
            </Select>
          </div>

          <div className="filter-group">
            <label>Items per page:</label>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              style={{ width: 120 }}
            >
              <Option value={25}>25</Option>
              <Option value={50}>50</Option>
              <Option value={100}>100</Option>
              <Option value={200}>200</Option>
            </Select>
          </div>
        </div>
      </Card>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <Card>
        <Table
          columns={columns}
          dataSource={useMemo(() => activeTab === 'active' ? uniqueMembers : uniqueArchivedMembers, [activeTab, uniqueMembers, uniqueArchivedMembers])}
          rowKey="phoneNumber"
          loading={isLoading}
          pagination={useMemo(() => ({
            current: currentPage,
            pageSize: pageSize,
            total: activeTab === 'active' ? total : archivedTotal,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} members`,
            onChange: handlePageChange,
            showQuickJumper: total > 100 || archivedTotal > 100,
          }), [currentPage, pageSize, activeTab, total, archivedTotal, handlePageChange])}
          scroll={{ x: 800 }}
          // Show loading indicator when fetching in background
          locale={{
            emptyText: isLoading ? <Skeleton active paragraph={{ rows: 5 }} /> : 'No members found',
          }}
        />
      </Card>

      {/* Groups Drawer */}
      <Drawer
        title={
          <div>
            <h3 style={{ margin: 0 }}>Groups for {selectedMember?.name}</h3>
            <p style={{ margin: '0.5rem 0 0 0', color: '#666', fontSize: '0.9rem' }}>
              {selectedMember?.phoneNumber} • {filteredDrawerGroups.length} of {memberGroups.length} groups
            </p>
          </div>
        }
        placement="right"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width={700}
        className="groups-drawer"
      >
        {(!memberDetailsData && memberGroups.length === 0 && selectedMember) ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Loader text="Loading groups..." />
          </div>
        ) : memberGroups.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            No groups found for this member
          </div>
        ) : (
          <>
            <div className="drawer-filters">
          <div className="drawer-filter-item">
            <label className="drawer-filter-label">Search</label>
            <Search
              placeholder="Search by group or account name..."
              allowClear
              value={drawerSearchTerm}
              onChange={handleDrawerSearchChange}
              prefix={<SearchOutlined />}
              style={{ width: '100%' }}
            />
          </div>
          <div className="drawer-filter-item">
            <label className="drawer-filter-label">Account</label>
            <Select
              value={drawerAccountFilter}
              onChange={setDrawerAccountFilter}
              style={{ width: '100%' }}
              allowClear={false}
            >
              <Option value="all">All Accounts</Option>
              {drawerAccountOptions.map((option) => (
                <Option key={option.key} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="drawer-filter-item">
            <label className="drawer-filter-label">Role</label>
            <Select
              value={drawerRoleFilter}
              onChange={setDrawerRoleFilter}
              style={{ width: '100%' }}
            >
              <Option value="all">All Roles</Option>
              <Option value="superAdmin">Super Admin</Option>
              <Option value="admin">Admin</Option>
              <Option value="member">Member</Option>
            </Select>
          </div>
        </div>
        <Table
          columns={drawerTableColumns}
          dataSource={filteredDrawerGroups}
          rowKey="groupId"
          pagination={false}
          size="small"
        />
          </>
        )}
      </Drawer>

      {/* Edit Modal */}
      <Modal
        title="Edit Member"
        open={editModalVisible}
        onOk={handleUpdateMember}
        onCancel={handleModalClose}
        okText="Save"
        cancelText="Cancel"
        okButtonProps={{ icon: <CheckOutlined /> }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            name: editingMember?.name || '',
            isAdmin: editingMember?.isAdmin || false,
            isSuperAdmin: editingMember?.isSuperAdmin || false,
          }}
        >
          <Form.Item label="Phone Number">
            <Input value={editingMember?.phoneNumber} disabled />
          </Form.Item>
          
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter member name' }]}
          >
            <Input placeholder="Enter member name" />
          </Form.Item>
          
          <Form.Item name="isAdmin" valuePropName="checked">
            <Checkbox>Is Admin</Checkbox>
          </Form.Item>
          
          <Form.Item name="isSuperAdmin" valuePropName="checked">
            <Checkbox>Is Super Admin</Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Members;

